import { Check, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import type { CheckpointResult, ScenarioDetail, ScenarioSummary } from "@/lib/scenarios";

type ResultPassedProps = {
  scenario: ScenarioDetail;
  result: CheckpointResult;
  // Newly-unlocked scenarios — shown as "теперь ты можешь" cards.
  unlocked: ScenarioSummary[];
  onDone: () => void;
};

/**
 * Passed result + Now-You-Can in one screen.
 *
 * Hero is the scenario's promise rendered against the amber gradient;
 * the score badge is small, since the celebratory framing is the
 * reward, not the percentage. Below: 1–3 scenarios that just unlocked
 * by this completion (resolved client-side from the journal list),
 * surfaced as click-through cards. Empty state: a single "В журнал"
 * CTA when nothing new is open.
 *
 * Mockup: `design/hifi/scenarios.jsx` `NowYouCanScreen` ~screen 5.
 */
export function ResultPassed({ scenario, result, unlocked, onDone }: ResultPassedProps) {
  const t = useT();
  const pct = Math.round(result.score * 100);
  return (
    <div
      className="flex min-h-100dvh flex-col rounded-[20px]"
      style={{
        background: "linear-gradient(180deg, var(--scen-bg) 0%, var(--scen-bg2) 100%)",
      }}
    >
      <div className="flex justify-end p-3.5">
        <button
          type="button"
          aria-label="close"
          onClick={onDone}
          className="rounded-md p-2 text-text-2 transition-colors hover:bg-white/40 hover:text-text"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="px-6 pt-3 pb-2">
        <div
          className="mb-5 inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-wider"
          style={{ color: "var(--scen-fg)", backdropFilter: "blur(8px)" }}
        >
          <Check className="size-3" />
          {t("skills.result_passed_title")} ·{" "}
          {t("skills.result_passed_score", {
            correct: String(result.correctCount),
            total: String(result.totalItems),
            pct: String(pct),
          })}
        </div>
        <h1 className="text-[32px] font-bold leading-[1.1] tracking-[-0.03em]" style={{ color: "var(--scen-ink)" }}>
          {scenario.title}
        </h1>
        <p className="mt-3 text-[14px] leading-[1.5]" style={{ color: "var(--scen-fg)" }}>
          {scenario.promise}
        </p>
      </div>

      {unlocked.length > 0 && (
        <div className="flex flex-col gap-2 px-6 pt-5 pb-4">
          <div className="font-mono text-[11px] uppercase tracking-wider" style={{ color: "var(--scen-fg)" }}>
            {t("skills.now_you_can_unlocked")}
          </div>
          {unlocked.slice(0, 3).map((s) => (
            <Link
              key={s.slug}
              to={`/skills/${s.slug}`}
              className="rounded-[var(--r-md)] bg-white/72 px-4 py-3 transition-colors hover:bg-white/85"
              style={{ backdropFilter: "blur(10px)" }}
            >
              <div className="text-[15px] font-semibold" style={{ color: "var(--scen-ink)" }}>
                {s.title}
              </div>
              <div className="mt-0.5 text-[12px]" style={{ color: "var(--scen-fg)" }}>
                {s.promise}
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="flex-1" />
      <div className="flex flex-col gap-2 rounded-t-[20px] border-t border-border bg-surface px-5 py-4">
        <Button type="button" variant="scenario" size="block" onClick={onDone}>
          {unlocked.length > 0 ? t("skills.now_you_can_continue") : t("skills.result_passed_cta_journal")}
        </Button>
      </div>
    </div>
  );
}
